import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { uniq } from 'ramda';
import sessionStorage from './sessionStorage';

const SessionContext = React.createContext();

function useSessionContext() {
  return useContext(SessionContext);
}

const initialState = () => sessionStorage.get();

// TODO: Add refresh
const SessionContextProvider = ({ children, state }) => {
  const [context, setContext] = useState(state ?? initialState() ?? {});

  const { data } = useQuery(
    gql`
      query Roles {
        me {
          id
          roles
        }
      }
    `,
    {
      // eslint-disable-next-line react/destructuring-assignment
      skip: !context.type || context.type === 'NONE' || context.type === 'NOROLES'
    }
  );

  // Use useEffect instead of onCompleted to avoid stale closure and Apollo deprecation warning
  // Only depend on 'data' to avoid infinite loop - use setState callback to access current context
  useEffect(() => {
    if (data?.me?.roles?.length) {
      const newRoles = uniq([...(data?.me?.roles || [])]);
      
      setContext(prevContext => {
        const currentRoles = prevContext.roles || [];
        
        // Only update if roles actually changed
        const rolesChanged = newRoles.length !== currentRoles.length ||
          newRoles.some((role, index) => role !== currentRoles[index]);
        
        if (rolesChanged) {
          const newContext = {
            ...prevContext,
            roles: newRoles
          };
          sessionStorage.set(newContext);
          return newContext;
        }
        
        return prevContext; // No change
      });
    }
  }, [data]); // ✅ Only depend on data, use setState callback to avoid infinite loop

  // Listen for session token updates from SSO popup to immediately refresh session context
  useEffect(() => {
    const handleTokenUpdate = (event) => {
      const { token } = event.detail || {};
      if (token) {
        console.log('🔄 SessionContext: Received token update from SSO, updating context');
        setContext(prevContext => {
          if (prevContext.token === token) {
            return prevContext;
          }
          const newContext = { ...prevContext, token };
          sessionStorage.set(newContext);
          return newContext;
        });
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('sessionTokenUpdated', handleTokenUpdate);
      return () => {
        window.removeEventListener('sessionTokenUpdated', handleTokenUpdate);
      };
    }
    return undefined;
  }, []);

  const { token } = context;

  const get = useCallback(
    async () => token,
    [token]
  );

  const set = useCallback(attrs => {
    setContext(prevContext => {
      const newContext = { ...prevContext, ...attrs };
      const hasChanges = Object.keys(attrs || {}).some(
        key => prevContext?.[key] !== newContext?.[key]
      );

      if (!hasChanges) {
        return prevContext;
      }

      sessionStorage.set(newContext);
      return newContext;
    });
  }, []);

  const replace = useCallback(async newAttrs => {
    const fallbackContext = {
      type: 'NONE',
      token: undefined,
      cartId: null,
      cartCount: null
    };

    setContext(prevContext => {
      const nextContext = newAttrs || fallbackContext;
      const sameKeys = Object.keys({ ...prevContext, ...nextContext });
      const isSame = sameKeys.every(key => prevContext?.[key] === nextContext?.[key]);

      if (isSame) {
        return prevContext;
      }

      // Reset Apollo cache when custId changes (ASM customer switch)
      if (prevContext?.custId && nextContext?.custId && prevContext.custId !== nextContext.custId) {
        // eslint-disable-next-line no-underscore-dangle
        const apolloClient = typeof window !== 'undefined' && window.__APOLLO_CLIENT__;
        if (apolloClient?.resetStore) {
          apolloClient.resetStore().catch(() => {});
        }
      }

      sessionStorage.set(nextContext);
      return nextContext;
    });
  }, []);

  const value = useMemo(
    () => ({
      ...context,
      get,
      set,
      replace
    }),
    [context, get, set, replace]
  );

  // TODO: Make false in production builds
  if (typeof window !== 'undefined' && window.Cypress) {
    window.sessionContext = value;
  }

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

SessionContextProvider.propTypes = {
  children: PropTypes.node,
  // eslint-disable-next-line react/forbid-prop-types
  state: PropTypes.object
};

export { SessionContextProvider, useSessionContext, SessionContext };
